/**
 * MCP Client Service for Yahoo Finance API
 * Connects to the remote Cloudflare Worker MCP server
 */

// Types
export interface StockQuote {
  symbol: string;
  name: string;
  price: number | null;
  change: number | null;
  changePercent: number | null;
  previousClose: number | null;
  dayHigh: number | null;
  dayLow: number | null;
  volume: number | null;
  marketCap: number | null;
  currency: string;
  exchange: string;
  marketState: string;
  fiftyTwoWeekHigh: number | null;
  fiftyTwoWeekLow: number | null;
}

export interface TrendingData {
  region: string;
  count: number;
  symbols: string[];
  jobTimestamp: string;
  startInterval: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  error?: string;
}

export interface TrendingResponse {
  success: boolean;
  data: TrendingData;
  error?: string;
}

export interface HistoricalDataPoint {
  date: string;
  timestamp: number;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  adjustedClose: number | null;
}

export interface HistoricalData {
  symbol: string;
  currency: string;
  exchangeName: string;
  instrumentType: string;
  firstTradeDate: string;
  regularMarketTime: string;
  gmtoffset: number;
  timezone: string;
  exchangeTimezoneName: string;
  regularMarketPrice: number;
  chartPreviousClose: number;
  previousClose: number;
  scale: number;
  priceHint: number;
  currentTradingPeriod: any;
  tradingPeriods: any;
  dataGranularity: string;
  range: string;
  validRanges: string[];
  historicalData: HistoricalDataPoint[];
}

export interface NewsItem {
  uuid: string;
  title: string;
  publisher: string;
  link: string;
  providerPublishTime: string;
  type: string;
  thumbnail: string | null;
  relatedTickers: string[];
}

export interface NewsData {
  symbol: string;
  region: string;
  news: NewsItem[];
}

export interface MarketIndex {
  symbol: string;
  name: string;
  price: number | null;
  change: number | null;
  changePercent: number | null;
  previousClose: number | null;
  dayHigh: number | null;
  dayLow: number | null;
  volume: number | null;
  currency: string;
  exchange: string;
  marketState: string;
  fiftyTwoWeekHigh: number | null;
  fiftyTwoWeekLow: number | null;
}

export interface CurrencyRate {
  fromCurrency: string;
  toCurrency: string;
  symbol: string;
  name: string;
  rate: number | null;
  change: number | null;
  changePercent: number | null;
  previousClose: number | null;
  dayHigh: number | null;
  dayLow: number | null;
  currency: string;
  exchange: string;
  marketState: string;
  lastUpdate: string;
}

// Supported regions and languages
export const SUPPORTED_REGIONS = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'IN', label: 'India' },
] as const;

export const SUPPORTED_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'es', label: 'Spanish' },
  { value: 'zh', label: 'Chinese' },
] as const;

// MCP Client Class
class MCPClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = 'https://yahoo-finance-mcp.kanosei.workers.dev';
  }

  /**
   * Get stock quotes from the remote MCP server
   */
  async getStockQuotes(
    symbols: string,
    region: string = 'US',
    language: string = 'en'
  ): Promise<ApiResponse<StockQuote[]>> {
    try {
      const url = `${this.baseUrl}/api/quotes?symbols=${encodeURIComponent(symbols)}&region=${region}&language=${language}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching stock quotes:', error);
      throw new Error(`Failed to fetch stock quotes: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get trending stocks from the remote MCP server
   */
  async getTrendingStocks(region: string = 'US'): Promise<TrendingResponse> {
    try {
      const url = `${this.baseUrl}/api/trending/${region}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching trending stocks:', error);
      throw new Error(`Failed to fetch trending stocks: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if the MCP server is healthy
   */
  async healthCheck(): Promise<{ status: string; message: string; timestamp: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error(`Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Call MCP tool directly (for advanced usage)
   */
  async callMCPTool(toolName: string, args: Record<string, any>): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/mcp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/call',
          params: {
            name: toolName,
            arguments: args,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error.message || 'MCP tool call failed');
      }

      return data.result;
    } catch (error) {
      console.error('MCP tool call failed:', error);
      throw new Error(`MCP tool call failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get historical data for a stock
   */
  async getHistoricalData(
    symbol: string,
    period1?: string,
    period2?: string,
    interval: string = '1d',
    region: string = 'US'
  ): Promise<ApiResponse<HistoricalData>> {
    try {
      let url = `${this.baseUrl}/api/historical?symbol=${encodeURIComponent(symbol)}&interval=${interval}&region=${region}`;
      
      if (period1) url += `&period1=${period1}`;
      if (period2) url += `&period2=${period2}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw new Error(`Failed to fetch historical data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get financial news
   */
  async getFinancialNews(
    symbol?: string,
    count: number = 10,
    region: string = 'US'
  ): Promise<ApiResponse<NewsData>> {
    try {
      let url = `${this.baseUrl}/api/news?count=${count}&region=${region}`;
      
      if (symbol) url += `&symbol=${encodeURIComponent(symbol)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching financial news:', error);
      throw new Error(`Failed to fetch financial news: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get market indices
   */
  async getMarketIndices(
    indices: string[] = ['^GSPC', '^DJI', '^IXIC'],
    region: string = 'US'
  ): Promise<ApiResponse<MarketIndex[]>> {
    try {
      const url = `${this.baseUrl}/api/indices?indices=${indices.join(',')}&region=${region}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching market indices:', error);
      throw new Error(`Failed to fetch market indices: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get currency exchange rates
   */
  async getCurrencyRates(
    fromCurrency: string,
    toCurrency: string,
    region: string = 'US'
  ): Promise<ApiResponse<CurrencyRate>> {
    try {
      const url = `${this.baseUrl}/api/currency?from=${fromCurrency}&to=${toCurrency}&region=${region}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching currency rates:', error);
      throw new Error(`Failed to fetch currency rates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * List available MCP tools
   */
  async listMCPTools(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/mcp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/list',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error.message || 'Failed to list MCP tools');
      }

      return data.result;
    } catch (error) {
      console.error('Failed to list MCP tools:', error);
      throw new Error(`Failed to list MCP tools: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Export singleton instance
export const mcpClient = new MCPClient();

// Types are already exported above
